import React, { useState } from "react";
import { Table, Badge, OverlayTrigger, Tooltip, Pagination, Button, ButtonGroup, Form } from "react-bootstrap";
import { FaEye, FaEdit, FaTrash, FaCheck, FaTimes, FaClock, FaExclamationTriangle } from "react-icons/fa";

const OcurrenciasTabla = ({
    ocurrencias,
    empleados,
    onVer,
    onEditar,
    onEliminar,
    itemsPerPage = 15,
    filtros,
    modoPreview = false,
    seleccionados = {},
    onToggleSeleccion,
    onToggleAll
}) => {
    const [currentPage, setCurrentPage] = useState(1);

    const obtenerNombreEmpleado = (empleadoId) => {
        const empleado = empleados.find((e) => e.dni === empleadoId || e.id === empleadoId);
        return empleado ? `${empleado.nombres} ${empleado.apellidos}` : empleadoId;
    };

    const formatearFecha = (fecha) => {
        if (!fecha) return "";
        try {
            if (typeof fecha === "string" && /^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
                const [year, month, day] = fecha.split("-");
                return `${day}/${month}/${year}`;
            }
            if (fecha.toDate) return fecha.toDate().toLocaleDateString("es-PE");
            if (fecha instanceof Date) return fecha.toLocaleDateString("es-PE");
            const date = new Date(fecha);
            if (isNaN(date.getTime())) return fecha.toString();
            return date.toLocaleDateString("es-PE");
        } catch {
            return fecha.toString();
        }
    };

    const formatearTiempo = (minutos) => {
        if (!minutos) return "0 min";
        const horas = Math.floor(minutos / 60);
        const mins = minutos % 60;
        return horas > 0 ? `${horas}h ${mins}min` : `${mins}min`;
    };

    const getBadgeVariant = (tipo) => {
        switch (tipo) {
            case "falta": return "danger";
            case "tardanza": return "warning";
            case "vacaciones": return "success";
            case "permiso": return "info";
            case "descanso_medico": return "secondary";
            case "licencia": return "primary";
            case "he_25": return "info";
            case "he_35": return "primary";
            case "he_100": return "success";
            default: return "light";
        }
    };

    const getEstadoBadge = (estado) => {
        switch (estado) {
            case "aprobado": return { variant: "success", icon: <FaCheck /> };
            case "rechazado": return { variant: "danger", icon: <FaTimes /> };
            case "pendiente": return { variant: "warning", icon: <FaClock /> };
            case "detectado_automatico": return { variant: "info", icon: <FaExclamationTriangle /> };
            case "manual": return { variant: "primary", icon: <FaCheck /> };
            case "manual_importado": return { variant: "secondary", icon: <FaCheck /> };
            default: return { variant: "secondary", icon: null };
        }
    };

    // Paginación
    const totalPages = Math.ceil(ocurrencias.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const ocurrenciasPaginadas = ocurrencias.slice(startIndex, endIndex);

    const handlePageChange = (page) => setCurrentPage(page);

    if (ocurrencias.length === 0) {
        return (
            <div className="table-responsive">
                <Table hover>
                    <thead className="bg-light">
                        <tr>
                            {modoPreview && <th style={{ width: '40px' }}></th>}
                            <th>Fecha</th>
                            <th>Empleado</th>
                            <th>Tipo</th>
                            <th>Estado</th>
                            <th>Justificado</th>
                            <th>Con Goce</th>
                            <th>Días/Horas</th>
                            <th>Tardanza</th>
                            <th>Motivo</th>
                            {!modoPreview && <th>Acciones</th>}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td colSpan={modoPreview ? "10" : "11"} className="text-center py-4">
                                {modoPreview
                                    ? "No se detectaron ocurrencias con los parámetros seleccionados"
                                    : filtros.periodo
                                        ? "No se encontraron ocurrencias con los filtros aplicados"
                                        : "Seleccione un período para ver las ocurrencias"
                                }
                            </td>
                        </tr>
                    </tbody>
                </Table>
            </div>
        );
    }

    return (
        <>
            <div className="table-responsive">
                <Table hover>
                    <thead className="bg-light">
                        <tr>
                            {modoPreview && (
                                <th style={{ width: '40px' }}>
                                    <Form.Check
                                        checked={Object.values(seleccionados).every(Boolean)}
                                        onChange={(e) => onToggleAll(e.target.checked)}
                                    />
                                </th>
                            )}
                            <th>Fecha</th>
                            <th>Empleado</th>
                            <th>Tipo</th>
                            <th>Estado</th>
                            <th>Justificado</th>
                            <th>Con Goce</th>
                            <th>Días/Horas</th>
                            <th>Tardanza</th>
                            <th>Motivo</th>
                            {!modoPreview && <th>Acciones</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {ocurrenciasPaginadas.map((ocurrencia, idx) => {
                            const estadoBadge = getEstadoBadge(ocurrencia.estado);
                            const globalIdx = startIndex + idx;
                            const isSelected = modoPreview ? seleccionados[globalIdx] : true;

                            return (
                                <tr
                                    key={ocurrencia.id || globalIdx}
                                    className={modoPreview && !isSelected ? "opacity-50" : ""}
                                >
                                    {modoPreview && (
                                        <td>
                                            <Form.Check
                                                checked={!!isSelected}
                                                onChange={() => onToggleSeleccion(globalIdx)}
                                            />
                                        </td>
                                    )}
                                    <td>{formatearFecha(ocurrencia.fecha)}</td>
                                    <td>
                                        <small className="fw-bold">{ocurrencia.empleado_id}</small>
                                        <br />
                                        {ocurrencia.empleado_nombre || obtenerNombreEmpleado(ocurrencia.empleado_id)}
                                    </td>
                                    <td>
                                        <Badge bg={getBadgeVariant(ocurrencia.tipo)}>
                                            {ocurrencia.tipo?.toUpperCase()}
                                        </Badge>
                                    </td>
                                    <td>
                                        <Badge bg={estadoBadge.variant}>
                                            {estadoBadge.icon} {ocurrencia.estado?.toUpperCase()}
                                        </Badge>
                                    </td>
                                    <td>
                                        <Badge bg={ocurrencia.justificado ? "success" : "danger"}>
                                            {ocurrencia.justificado ? "SÍ" : "NO"}
                                        </Badge>
                                    </td>
                                    <td>
                                        <Badge bg={ocurrencia.con_goce ? "success" : "warning"}>
                                            {ocurrencia.con_goce ? "SÍ" : "NO"}
                                        </Badge>
                                    </td>
                                    <td>
                                        {ocurrencia.dias_afectados > 0 && <span>{ocurrencia.dias_afectados} días</span>}
                                        {ocurrencia.horas_afectadas > 0 && <span>{ocurrencia.horas_afectadas} hrs</span>}
                                    </td>
                                    <td>
                                        {ocurrencia.minutos_tardanza > 0 ? formatearTiempo(ocurrencia.minutos_tardanza) : "-"}
                                    </td>
                                    <td>
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={<Tooltip>{ocurrencia.motivo || "Sin motivo"}</Tooltip>}
                                        >
                                            <span>
                                                {ocurrencia.motivo?.length > 30
                                                    ? `${ocurrencia.motivo.substring(0, 30)}...`
                                                    : ocurrencia.motivo || "-"}
                                            </span>
                                        </OverlayTrigger>
                                    </td>
                                    {!modoPreview && (
                                        <td>
                                            <ButtonGroup size="sm">
                                                <Button variant="outline-info" onClick={() => onVer(ocurrencia)}>
                                                    <FaEye />
                                                </Button>
                                                <Button variant="outline-warning" onClick={() => onEditar(ocurrencia)}>
                                                    <FaEdit />
                                                </Button>
                                                <Button variant="outline-danger" onClick={() => onEliminar(ocurrencia.id)}>
                                                    <FaTrash />
                                                </Button>
                                            </ButtonGroup>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </div>

            {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-3">
                    <Pagination>
                        <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                        {[...Array(totalPages)].map((_, index) => {
                            const pageNumber = index + 1;
                            const isCurrentPage = pageNumber === currentPage;
                            const isNearCurrent = Math.abs(pageNumber - currentPage) <= 2 || pageNumber === 1 || pageNumber === totalPages;

                            if (!isNearCurrent && pageNumber !== 2 && pageNumber !== totalPages - 1) {
                                if (pageNumber === 3 && currentPage > 4) return <Pagination.Ellipsis key={pageNumber} />;
                                if (pageNumber === totalPages - 2 && currentPage < totalPages - 3) return <Pagination.Ellipsis key={pageNumber} />;
                                return null;
                            }

                            return (
                                <Pagination.Item key={pageNumber} active={isCurrentPage} onClick={() => handlePageChange(pageNumber)}>
                                    {pageNumber}
                                </Pagination.Item>
                            );
                        })}
                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                        <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                    </Pagination>
                </div>
            )}

            {ocurrencias.length > itemsPerPage && (
                <div className="text-center mt-2 text-muted small">
                    Mostrando {startIndex + 1}-{Math.min(endIndex, ocurrencias.length)} de {ocurrencias.length} ocurrencias
                </div>
            )}
        </>
    );
};

export default OcurrenciasTabla;
